import { createReference, MedplumClient, ProfileResource } from "@medplum/core";
import { Attachment, Bundle, BundleEntry, Communication, Provenance } from "@medplum/fhirtypes";

import { CommunicationInbox } from "@/store/slices/inbox";
import { getResourcesFromBundle } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import { createAttachmentWithHash } from "@/utils/uploads";

import { COMMUNICATION_INBOX_CATEGORY } from "../constants/messages";
import { getCommunicationReadProvenance, getCommunicationThreadQuery } from "../utils/messages";

const COMMUNICATION_STATUS_MAX_RETRIES = 3;

export async function fetchCommunication(
  medplum: MedplumClient,
  communicationId: CommunicationInbox["id"],
): Promise<Communication | undefined> {
  return await medplum.searchOne(
    "Communication",
    {
      _id: communicationId,
      status: "in-progress,completed",
      category: COMMUNICATION_INBOX_CATEGORY,
    },
    { cache: "no-cache" },
  );
}

export async function fetchLatestThread(
  medplum: MedplumClient,
  threadId: string,
): Promise<Communication> {
  return await medplum.readResource("Communication", threadId, { cache: "no-cache" });
}

export async function updateCommunicationStatus(
  medplum: MedplumClient,
  communication: Communication,
  status: Communication["status"],
  retries = 0,
) {
  let updatedCommunication: Communication | undefined;
  try {
    updatedCommunication = await medplum.patchResource("Communication", communication.id!, [
      {
        op: "test",
        path: "/meta/versionId",
        value: communication.meta?.versionId,
      },
      {
        op: "replace",
        path: "/status",
        value: status,
      },
    ]);
  } catch (error) {
    updatedCommunication = await medplum.readResource("Communication", communication.id!);
    if (!updatedCommunication) {
      showSystemErrorNotification();
      throw new Error(`Communication not found: ${communication.id}`);
    }

    if (retries >= COMMUNICATION_STATUS_MAX_RETRIES) {
      console.error(`Unable to update communication status: ${communication.id}`);
      return updatedCommunication;
    }

    if (updatedCommunication?.status !== status) {
      return updateCommunicationStatus(medplum, updatedCommunication, status, retries + 1);
    }
  }

  return updatedCommunication;
}

export async function setRelatedCommunicationsAsRead(
  medplum: MedplumClient,
  communication: Communication,
) {
  const profile = medplum.getProfile();
  if (!profile) return;
  const profileReference = createReference(profile);

  const unreadCommunications = await fetchUnreadCommunications(medplum, profile, communication);
  const entry: BundleEntry<Provenance>[] = [];
  const occurredDateTime = new Date().toISOString();
  for (const unreadCommunication of unreadCommunications) {
    entry.push({
      request: {
        method: "POST",
        url: "Provenance",
      },
      resource: getCommunicationReadProvenance(unreadCommunication, profileReference, {
        occurredDateTime,
      }),
    });
  }

  if (entry.length === 0) return;

  return await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry,
  });
}

export async function setCommunicationAsRead(medplum: MedplumClient, communication: Communication) {
  const profile = medplum.getProfile();
  if (!profile) return;

  const unreadCommunications = await fetchUnreadCommunications(medplum, profile, communication, {
    _id: communication.id,
    "part-of": communication.partOf?.[0]?.reference,
  });
  if (unreadCommunications.length === 0) return;

  const provenance = getCommunicationReadProvenance(communication, createReference(profile));
  return await medplum.createResource(provenance);
}

export async function uploadAttachments(
  medplum: MedplumClient,
  files: File[],
): Promise<Attachment[]> {
  const profile = medplum.getProfile();
  if (!profile) return [];

  const attachments: Attachment[] = await Promise.all(
    files.map(
      async (file) =>
        await createAttachmentWithHash(medplum, {
          data: file,
          contentType: file.type,
          filename: file.name,
        }),
    ),
  );

  return attachments;
}

export async function fetchUnreadCommunications(
  medplum: MedplumClient,
  profile: ProfileResource,
  communication: Communication,
  extra?: Record<string, string | number | boolean | undefined>,
) {
  const profileReference = createReference(profile);

  const query = getCommunicationThreadQuery(communication, profile, extra);
  const response = await medplum.searchResources("Communication", query, { cache: "no-cache" });
  const bundle = response.bundle as Bundle<Communication | Provenance>;

  const provenanceByTarget = (getResourcesFromBundle("Provenance", bundle) as Provenance[]).reduce(
    (acc, provenance) => {
      const { reference: targetReference } = provenance.target?.[0] ?? {};
      if (!targetReference) return acc;

      const { reference: agentReference } = provenance.agent?.[0]?.who ?? {};
      if (!agentReference || agentReference !== profileReference.reference) return acc;

      acc[targetReference] = provenance;
      return acc;
    },
    {} as Record<string, Provenance>,
  );

  return (getResourcesFromBundle("Communication", bundle) as Communication[]).filter(
    (communication) => !provenanceByTarget[createReference(communication).reference],
  );
}
